/**
 * ZonePaletteDialog — admin editor for per-tenant zone colours + labels.
 *
 * Opens from the Locations page toolbar. Shows every currently-configured
 * zone as a coloured row plus an "add" input at the bottom. Seed-defaults
 * button prefills common cold-chain / hazmat / bulk zones.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Palette, Plus, Trash, Sparkle } from '@phosphor-icons/react';
import {
  listZonePalette, saveZonePaletteEntry, deleteZonePaletteEntry, seedZonePaletteDefaults,
} from '../lib/api';

const PRESETS = ['#0EA5E9', '#1D4ED8', '#F97316', '#F59E0B', '#84CC16', '#22C55E', '#14B8A6', '#A855F7', '#EC4899', '#F43F5E', '#78716C', '#525252'];

export default function ZonePaletteDialog({ open, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newRow, setNewRow] = useState({ zone_code: '', label: '', color: '#0EA5E9', icon: '' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await listZonePalette();
      setRows(r.data || []);
    } catch (e) {
      toast.error('Could not load palette');
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const save = async (zoneCode, patch, msg = 'Saved') => {
    const target = rows.find((r) => r.zone_code === zoneCode);
    if (!target) return;
    setBusy(true);
    try {
      const body = { zone_code: target.zone_code, label: target.label || '', color: target.color, icon: target.icon || '', ...patch };
      await saveZonePaletteEntry(zoneCode, body);
      await load();
      onSaved?.();
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const remove = async (zoneCode) => {
    if (!window.confirm(`Remove "${zoneCode}" from the palette?`)) return;
    setBusy(true);
    try {
      await deleteZonePaletteEntry(zoneCode);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  const addNew = async () => {
    const code = (newRow.zone_code || '').trim().toUpperCase();
    if (!code) { toast.error('Zone code is required'); return; }
    if (!/^#[0-9A-Fa-f]{6}$/.test(newRow.color)) { toast.error('Color must be a hex like #F97316'); return; }
    setBusy(true);
    try {
      await saveZonePaletteEntry(code, { ...newRow, zone_code: code });
      setNewRow({ zone_code: '', label: '', color: '#0EA5E9', icon: '' });
      await load();
      onSaved?.();
      toast.success(`Zone ${code} added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    } finally { setBusy(false); }
  };

  const seed = async () => {
    setBusy(true);
    try {
      const r = await seedZonePaletteDefaults();
      await load();
      onSaved?.();
      toast.success(r.data?.message || 'Seeded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="zone-palette-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette size={20} weight="fill" /> Zone palette
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Map every zone code to a colour + label so operators see chilled / hazmat / bulk zones at a glance.
          </p>
        </DialogHeader>

        <div className="space-y-2">
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
              No zones configured yet.
              <Button size="sm" onClick={seed} className="ml-2" disabled={busy} data-testid="palette-seed-defaults">
                <Sparkle size={14} className="mr-1" /> Seed defaults
              </Button>
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="palette-rows">
              {rows.map((row) => (
                <div key={row.zone_code} className="flex items-center gap-2 border border-border p-2" data-testid={`palette-row-${row.zone_code}`}>
                  <span
                    className="px-2 py-1 text-[11px] font-black uppercase tracking-widest shrink-0"
                    style={{ backgroundColor: row.color, color: '#fff', minWidth: 88, textAlign: 'center' }}
                  >
                    {row.icon} {row.zone_code}
                  </span>
                  <Input
                    value={row.label || ''}
                    placeholder="Friendly label"
                    onChange={(e) => setRows((prev) => prev.map((r) => r.zone_code === row.zone_code ? { ...r, label: e.target.value } : r))}
                    onBlur={() => save(row.zone_code, { label: row.label }, '')}
                    className="flex-1 h-8"
                    data-testid={`palette-label-${row.zone_code}`}
                  />
                  <Input
                    type="color"
                    value={row.color}
                    onChange={(e) => setRows((prev) => prev.map((r) => r.zone_code === row.zone_code ? { ...r, color: e.target.value } : r))}
                    onBlur={() => save(row.zone_code, { color: row.color }, '')}
                    className="w-14 h-8 p-1 cursor-pointer"
                    data-testid={`palette-color-${row.zone_code}`}
                  />
                  <Input
                    value={row.icon || ''}
                    placeholder="Icon"
                    maxLength={4}
                    onChange={(e) => setRows((prev) => prev.map((r) => r.zone_code === row.zone_code ? { ...r, icon: e.target.value } : r))}
                    onBlur={() => save(row.zone_code, { icon: row.icon }, '')}
                    className="w-16 h-8 text-center"
                    data-testid={`palette-icon-${row.zone_code}`}
                  />
                  <Button variant="outline" size="sm" onClick={() => remove(row.zone_code)} className="text-red-600 border-red-200" disabled={busy}>
                    <Trash size={12} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-border pt-3 mt-2">
          <Label className="text-xs font-bold">Add zone</Label>
          <div className="flex items-center gap-2 mt-1.5">
            <Input placeholder="CODE" value={newRow.zone_code}
              onChange={(e) => setNewRow({ ...newRow, zone_code: e.target.value.toUpperCase() })}
              className="w-24 h-8 font-mono uppercase" data-testid="palette-new-code" />
            <Input placeholder="Label" value={newRow.label}
              onChange={(e) => setNewRow({ ...newRow, label: e.target.value })} className="flex-1 h-8" />
            <div className="flex gap-1">
              {PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewRow({ ...newRow, color: c })}
                  className={`w-6 h-6 border-2 ${newRow.color === c ? 'border-foreground' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                  aria-label={c}
                  data-testid={`palette-preset-${c.replace('#', '')}`}
                />
              ))}
            </div>
            <Input placeholder="Icon" maxLength={4} value={newRow.icon}
              onChange={(e) => setNewRow({ ...newRow, icon: e.target.value })}
              className="w-16 h-8 text-center" />
            <Button size="sm" onClick={addNew} disabled={busy || !newRow.zone_code} data-testid="palette-add-btn">
              <Plus size={12} className="mr-1" /> Add
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={seed} disabled={busy}>
            <Sparkle size={12} className="mr-1" /> Seed defaults
          </Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
